import { useEffect, useMemo, useRef, useState } from "react";

import type { NgwWebmapItem } from "@nextgis/ngw-kit";

import { useLayerLegendSymbols } from "../hooks/useLayerLegendSymbols";
import { useLayerVisibility } from "../hooks/useLayerVisibility";
import { useTranslation } from "../i18n";
import {
  type LayerTreeOptions,
  getLayerTitle,
  isHiddenLayer,
  setBranchVisibility,
  setLeafVisibility,
} from "../utils/layers";

import { LayerLegend } from "./LayerLegend";
import { LegendSymbolIcon } from "./LegendSymbolIcon";

import "./LayerNode.css";

export function LayerNode({
  layer,
  parentExclusive,
  treeOptions,
}: {
  layer: NgwWebmapItem;
  parentExclusive: boolean;
  treeOptions: LayerTreeOptions;
}) {
  const { t } = useTranslation();
  const item = layer.item;
  const isGroup = item?.item_type === "group" || item?.item_type === "root";
  const isGroupExclusive = item?.item_type === "group" && item.group_exclusive;
  const isHidden = !item || isHiddenLayer(item, treeOptions);
  const children = useMemo(() => {
    if (!isGroup) return [];
    return layer.tree
      .getChildren()
      .filter((child) => !isHiddenLayer(child.item, treeOptions))
      .toReversed();
  }, [isGroup, layer, treeOptions]);
  const [expanded, setExpanded] = useState(
    item?.item_type === "group" ? item.group_expanded !== false : false,
  );
  const [legendExpanded, setLegendExpanded] = useState(
    layer.item.item_type === "layer" && layer.item.legend_symbols === "expand",
  );
  const input = useRef<HTMLInputElement>(null);
  const { visible, indeterminate } = useLayerVisibility(layer, treeOptions);
  const title = getLayerTitle(item, t("layers.defaultTitle"));
  const legendSymbols = useLayerLegendSymbols(layer, !isGroup && !isHidden);
  const singleLegendSymbol =
    legendSymbols?.length === 1 ? legendSymbols[0] : undefined;
  const hasMultipleLegendSymbols = Boolean(
    legendSymbols && legendSymbols.length > 1,
  );

  useEffect(() => {
    if (input.current) {
      input.current.indeterminate =
        input.current.type === "checkbox" && indeterminate;
    }
  }, [indeterminate]);

  if (isHidden || (isGroup && !children.length)) {
    return null;
  }

  return (
    <div className={isGroup ? "layer-node layer-node-group" : "layer-node"}>
      <div className="layer-line">
        <button
          type="button"
          className={
            isGroup || hasMultipleLegendSymbols
              ? "layer-toggle"
              : "layer-toggle layer-toggle-empty"
          }
          disabled={!isGroup && !hasMultipleLegendSymbols}
          tabIndex={isGroup || hasMultipleLegendSymbols ? 0 : -1}
          aria-label={
            isGroup
              ? t(expanded ? "layers.collapse" : "layers.expand", { title })
              : t(
                  legendExpanded
                    ? "layers.legendCollapse"
                    : "layers.legendExpand",
                  { title },
                )
          }
          onClick={() => {
            if (isGroup) {
              setExpanded((value) => !value);
            } else {
              setLegendExpanded((value) => !value);
            }
          }}
        >
          {isGroup
            ? expanded
              ? "-"
              : "+"
            : hasMultipleLegendSymbols
              ? legendExpanded
                ? "-"
                : "+"
              : ""}
        </button>
        <input
          ref={input}
          type="checkbox"
          className={
            parentExclusive
              ? "layer-check layer-check-exclusive"
              : "layer-check"
          }
          checked={visible}
          title={title}
          onChange={(event) => {
            if (isGroup) {
              setBranchVisibility(
                layer,
                event.currentTarget.checked,
                treeOptions,
              );
            } else {
              setLeafVisibility(
                layer,
                event.currentTarget.checked,
                treeOptions,
              );
            }
          }}
        />
        <span
          className={
            singleLegendSymbol
              ? "layer-title layer-title-with-symbol"
              : "layer-title"
          }
        >
          {singleLegendSymbol ? (
            <>
              <LegendSymbolIcon symbol={singleLegendSymbol} />
              <span className="layer-title-text">{title}</span>
            </>
          ) : (
            title
          )}
        </span>
      </div>

      {!isGroup &&
        legendExpanded &&
        legendSymbols &&
        legendSymbols.length > 1 && <LayerLegend symbols={legendSymbols} />}

      {isGroup && (
        <div className="layer-children" hidden={!expanded}>
          {children.map((child) => (
            <LayerNode
              key={child.id}
              layer={child}
              parentExclusive={isGroupExclusive}
              treeOptions={treeOptions}
            />
          ))}
        </div>
      )}
    </div>
  );
}
